/**
 * Enhanced Device Family Component
 * Modern UI with improved organization and purple theme
 */

import React, { useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';

import { getAllDeviceFamilies } from '../api/device_family';
import DeviceFamilyForm from './AddDeviceFamilyForm';
import ModifyDeviceFamilyForm from './ModifyDeviceFamilyForm';
import { Button } from './ui/Button';
import { StatusBadge } from './ui/StatusBadge';
import { LoadingSkeleton } from './ui/LoadingSkeleton';
import { colors } from '../theme/colors';

interface DeviceFamilyData {
  id: number;
  device_name: string;
  adding_time: string;
  description: string;
  is_active: boolean;
}

const StyledDataGrid = styled(DataGrid)({
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: 16,
  backgroundColor: colors.background.paper,
  boxShadow: 'none',
  fontFamily: 'inherit',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: colors.neutral[50],
    color: colors.text.primary,
    borderBottom: `2px solid ${colors.neutral[200]}`,
    borderRadius: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    minHeight: '56px !important',
    maxHeight: '56px !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: colors.text.secondary,
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-row': {
    transition: 'all 0.15s ease-in-out',
    cursor: 'pointer',
    minHeight: '72px !important',
    '&:hover': {
      backgroundColor: colors.neutral[50],
      boxShadow: 'inset 3px 0 0 0 ' + colors.primary.main,
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${colors.neutral[100]}`,
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    fontSize: '0.9375rem',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `2px solid ${colors.neutral[200]}`,
    backgroundColor: colors.neutral[50],
    minHeight: '60px',
  },
  '& .MuiTablePagination-root': {
    color: colors.text.secondary,
  },
});

const HeaderBox = styled(Box)({
  padding: '32px 0',
  marginBottom: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const DeviceFamilyEnhanced: React.FC = () => {
  const [data, setData] = useState<DeviceFamilyData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getAllDeviceFamilies();
      setData(response || []);
    } catch (error) {
      console.error('Error fetching device families:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (id: number) => {
    setSelectedFamilyId(id);
    setIsEditDialogOpen(true);
  };

  const renderStatusBadge = (params: GridRenderCellParams) => {
    const status = params.row.is_active as boolean;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <StatusBadge status={status ? 'active' : 'inactive'} label={status ? 'Активно' : 'Неактивно'} />
      </Box>
    );
  };

  const renderEditButton = (params: GridRenderCellParams) => (
    <Tooltip title="Изменить семейство">
      <IconButton
        onClick={() => handleEdit(params.row.id as number)}
        disabled={isLoading}
        size="small"
        sx={{
          color: colors.secondary.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            color: colors.secondary.dark,
            transform: 'scale(1.1)',
            backgroundColor: 'rgba(38, 166, 154, 0.1)',
          },
        }}
      >
        <EditIcon />
      </IconButton>
    </Tooltip>
  );

  const columns: GridColDef[] = [
    {
      field: 'device_name',
      headerName: 'Семейство',
      flex: 1,
      minWidth: 280,
      renderCell: (params) => (
        <Box>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              color: colors.text.primary,
              fontWeight: 500,
              mb: 0.25,
            }}
          >
            {params.value}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: colors.text.secondary,
            }}
          >
            {params.row.description || 'Без описания'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.875rem',
            color: colors.text.secondary,
          }}
        >
          #{params.value}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Статус',
      width: 130,
      renderCell: renderStatusBadge,
    },
    {
      field: 'adding_time',
      headerName: 'Дата добавления',
      width: 180,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: colors.text.secondary,
          }}
        >
          {params.value
            ? new Date(params.value).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: renderEditButton,
    },
  ];

  if (isLoading && data.length === 0) {
    return (
      <Box sx={{ m: 4 }}>
        <LoadingSkeleton type="table" rows={8} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 4, py: 5 }}>
      <HeaderBox>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: 1,
              fontSize: '2rem',
            }}
          >
            Семейства устройств
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              color: colors.text.secondary,
              fontWeight: 400,
            }}
          >
            {data.length}
          </Typography>
        </Box>
        <Button variant="gradient" startIcon={<AddCircleOutlineIcon />} onClick={() => setIsAddDialogOpen(true)}>
          Добавить семейство
        </Button>
      </HeaderBox>

      <Box sx={{ width: '100%' }}>
        <StyledDataGrid
          rows={data}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'adding_time', sort: 'desc' }],
            },
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          loading={isLoading}
          autoHeight
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-row': {
              minHeight: '72px !important',
            },
          }}
        />
      </Box>

      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 4 }}>
          <DeviceFamilyForm onSuccess={fetchData} />
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Изменить семейство устройств
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4, pt: 2 }}>
          {selectedFamilyId && <ModifyDeviceFamilyForm device_family_id={selectedFamilyId} onSuccess={fetchData} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DeviceFamilyEnhanced;
